/**
 * Submit a form once at a time and keep why it failed (ACCT-06). Forms used to fire their async
 * submit without awaiting it: a refused save (e.g. a 422 for the state code) showed nothing, and a
 * double click sent the request twice.
 */

import { useRef, useState } from "react";

import { describeError } from "./errors";

export function useFormSubmit<T>(submit: (values: T) => Promise<unknown>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A second click in the same tick sees the old `busy`; the ref doesn't.
  const inFlight = useRef(false);

  const run = async (values: T) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      await submit(values);
    } catch (err) {
      setError(describeError(err));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return { busy, error, setError, run };
}
